import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

export default function Header() {
  const [menuItems, setMenuItems] = useState([])
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isServicesOpen, setIsServicesOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    // Fetch menu items from CMS
    fetch('https://api.dev.auxo.dk/public/content/menu_items')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          // Filter out App menu item and sort items by order
          const filtered = data.filter(item => item.label.toLowerCase() !== 'app' && item.href !== '/app')
          const sorted = [...filtered].sort((a, b) => (a.order || 0) - (b.order || 0))
          setMenuItems(sorted)
        }
      })
      .catch(err => console.error('Error fetching menu items:', err))
  }, [])

  // Close mobile menu on path change
  useEffect(() => {
    setIsMobileMenuOpen(false)
    setIsServicesOpen(false)
  }, [location.pathname])

  // Group menu items into main and sub-items
  const mainItems = menuItems.filter(item => !item.parent_label)
  const getSubItems = (parentLabel) => menuItems.filter(item => item.parent_label === parentLabel)

  const isActive = (path) => {
    if (path === '/' && location.pathname !== '/') return false
    return location.pathname.startsWith(path)
  }

  return (
    <>
      {/* Top Bar with Contact Info */}
      <div className="bg-black text-gray-300 text-xs border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center justify-end gap-6">
          <a href="tel:+4530345096" className="flex items-center gap-2 hover:text-[#5cbe4f] transition-colors">
            <i className="fa-solid fa-phone text-[#5cbe4f]"></i>
            <span className="font-medium">30 34 50 96</span>
          </a>
          <a href="mailto:frejlevauto@gmail.com" className="flex items-center gap-2 hover:text-[#5cbe4f] transition-colors">
            <i className="fa-solid fa-envelope text-[#5cbe4f]"></i>
            <span>frejlevauto@gmail.com</span>
          </a>
          <a 
            href="https://maps.app.goo.gl/NYxKMqLFdfv6L8976" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="hidden sm:flex items-center gap-2 hover:text-[#5cbe4f] transition-colors"
          >
            <i className="fa-solid fa-location-dot text-[#5cbe4f]"></i>
            <span>Svenstrupvej 39, 9200 Aalborg SV</span>
          </a>
        </div>
      </div>

      <header className="sticky top-0 z-40 bg-zinc-950/95 backdrop-blur-md border-b border-zinc-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 md:h-24 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="https://assets.auxodev.dk/assets/48e1c649-5942-45fb-ad5e-8c057f6b1ff1/frejlev-auto-black-logo_1.png"
              alt="Frejlev Auto"
              className="h-14 sm:h-16 md:h-20 w-auto transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation (>= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-8">
            {mainItems.map((item) => {
              const subItems = getSubItems(item.label)
              const hasSubItems = subItems.length > 0

              if (hasSubItems) {
                return (
                  <div key={item.id} className="relative group">
                    <button
                      className={`flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wider transition-colors cursor-pointer py-2 ${
                        subItems.some(sub => isActive(sub.href)) ? 'text-[#5cbe4f]' : 'text-gray-200 hover:text-[#5cbe4f]'
                      }`}
                    >
                      <span>{item.label}</span>
                      <i className="fa-solid fa-chevron-down text-[10px] transition-transform group-hover:rotate-180"></i>
                    </button>
                    
                    {/* Dropdown Menu */}
                    <div className="absolute top-full left-0 mt-1 w-56 bg-zinc-900 border border-zinc-750 rounded-lg shadow-2xl py-2 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                      {subItems.map((sub) => (
                        <Link
                          key={sub.id}
                          to={sub.href}
                          className={`block px-4 py-2.5 text-sm transition-colors ${
                            isActive(sub.href)
                              ? 'bg-[#5cbe4f]/20 text-[#5cbe4f] font-semibold'
                              : 'text-gray-300 hover:bg-zinc-800 hover:text-white'
                          }`}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )
              }

              return (
                <Link
                  key={item.id}
                  to={item.href}
                  className={`text-sm font-semibold uppercase tracking-wider transition-colors py-2 relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#5cbe4f] after:scale-x-0 hover:after:scale-x-100 after:transition-transform ${
                    isActive(item.href) ? 'text-[#5cbe4f] after:scale-x-100' : 'text-gray-200 hover:text-[#5cbe4f]'
                  }`}
                >
                  {item.label}
                </Link>
              )
            })}

            {/* Call CTA */}
            <a
              href="tel:+4530345096"
              className="bg-[#5cbe4f] hover:bg-[#4ca83e] text-white text-sm font-bold px-5 py-2.5 rounded-lg transition-all flex items-center gap-2 shadow-md shadow-[#5cbe4f]/20 uppercase tracking-wide"
            >
              <i className="fa-solid fa-phone"></i>
              <span>30 34 50 96</span>
            </a>
          </nav>

          {/* Hamburger Menu Button (< 1200px) */}
          <div className="flex items-center gap-3 min-[1200px]:hidden">
            <a
              href="tel:+4530345096"
              className="bg-[#5cbe4f] text-white p-2.5 rounded-lg text-sm flex items-center justify-center hover:bg-[#4ca83e] transition-colors"
              aria-label="Ring til os"
            >
              <i className="fa-solid fa-phone"></i>
            </a>
            
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-300 hover:text-white focus:outline-none p-2.5 rounded-lg bg-zinc-800/80 border border-zinc-700 cursor-pointer"
              aria-label="Åben menu"
            >
              <i className={`fa-solid ${isMobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl w-6 h-6 flex items-center justify-center`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Sidebar (< 1200px) */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-50 min-[1200px]:hidden">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            
            {/* Sidebar Panel */}
            <div className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-zinc-950 border-l border-zinc-800 shadow-2xl flex flex-col p-6 overflow-y-auto animate-in slide-in-from-right duration-300">
              {/* Header */}
              <div className="flex items-center justify-between pb-6 border-b border-zinc-800 mb-6">
                <span className="text-lg font-bold text-white">Menu</span>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-400 hover:text-white focus:outline-none p-2 rounded-lg bg-zinc-900 border border-zinc-800 cursor-pointer"
                  aria-label="Luk menu"
                >
                  <i className="fa-solid fa-xmark text-xl w-6 h-6 flex items-center justify-center"></i>
                </button>
              </div>

              {/* Navigation Links */}
              <div className="space-y-2 flex-1">
                {mainItems.map((item) => {
                  const subItems = getSubItems(item.label)
                  const hasSubItems = subItems.length > 0

                  if (hasSubItems) {
                    return (
                      <div key={item.id} className="space-y-1">
                        <button
                          onClick={() => setIsServicesOpen(!isServicesOpen)}
                          className={`w-full flex items-center justify-between px-3 py-3 rounded-lg text-base font-semibold transition-colors ${
                            subItems.some(sub => isActive(sub.href)) ? 'bg-[#5cbe4f]/20 text-[#5cbe4f]' : 'text-gray-300 hover:bg-zinc-900 hover:text-white'
                          }`}
                        >
                          <span>{item.label}</span>
                          <i className={`fa-solid fa-chevron-down text-xs transition-transform ${isServicesOpen ? 'rotate-180' : ''}`}></i>
                        </button>
                        
                        {isServicesOpen && (
                          <div className="pl-4 space-y-1 bg-zinc-900/50 rounded-lg py-1 border-l-2 border-zinc-800">
                            {subItems.map((sub) => (
                              <Link
                                key={sub.id}
                                to={sub.href}
                                className={`block px-4 py-2.5 rounded-md text-sm transition-colors ${
                                  isActive(sub.href)
                                    ? 'text-[#5cbe4f] font-semibold bg-[#5cbe4f]/10'
                                    : 'text-gray-400 hover:text-white hover:bg-zinc-900'
                                }`}
                              >
                                {sub.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  }

                  return (
                    <Link
                      key={item.id}
                      to={item.href}
                      className={`block px-3 py-3 rounded-lg text-base font-semibold transition-colors ${
                        isActive(item.href)
                          ? 'bg-[#5cbe4f]/20 text-[#5cbe4f]'
                          : 'text-gray-300 hover:bg-zinc-900 hover:text-white'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )
                })}
              </div>

              {/* Footer / Call CTA */}
              <div className="pt-6 border-t border-zinc-800 mt-6 space-y-3">
                <a
                  href="tel:+4530345096"
                  className="w-full bg-[#5cbe4f] hover:bg-[#4ca83e] text-white text-base font-bold py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#5cbe4f]/20"
                >
                  <i className="fa-solid fa-phone"></i>
                  <span>Ring op: 30 34 50 96</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Floating Quick Action Buttons on Right Edge */}
      <aside aria-label="Hurtig kontakt" className="fixed right-0 top-1/2 -translate-y-1/2 z-40 hidden sm:flex flex-col gap-1.5 shadow-xl">
        <a
          href="tel:+4530345096"
          className="bg-[#5cbe4f] hover:bg-[#4ca83e] text-white p-3 rounded-l-lg transition-all flex items-center gap-2 group hover:pr-4"
          title="Ring til 30 34 50 96"
          aria-label="Ring til 30 34 50 96"
        >
          <i className="fa-solid fa-phone text-lg"></i>
          <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 text-sm font-semibold whitespace-nowrap">
            30 34 50 96
          </span>
        </a>
        <a
          href="mailto:frejlevauto@gmail.com"
          className="bg-[#5cbe4f] hover:bg-[#4ca83e] text-white p-3 rounded-l-lg transition-all flex items-center gap-2 group hover:pr-4"
          title="Send e-mail"
          aria-label="Send e-mail til frejlevauto@gmail.com"
        >
          <i className="fa-solid fa-envelope text-lg"></i>
          <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 text-sm font-semibold whitespace-nowrap">
            Send e-mail
          </span>
        </a>
      </aside>
    </>
  )
}
